import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ApiError } from '@/shared/api/client';
import {
  anniversarySettings,
  parametrization,
  type AnniversaryModality,
  type AnniversarySettings,
  type AnniversaryVersion,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatDateTime } from '@/shared/lib/format';
import { LinkComIcone } from '@/shared/ui/AcaoComIcone';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { DataTable } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { IconChip } from '@/shared/ui/IconChip';
import { PageHeader } from '@/shared/ui/PageHeader';
import { RichTextEditor, type RichTextEditorHandle } from '@/shared/ui/RichTextEditor';
import { Select } from '@/shared/ui/Select';
import { Skeleton } from '@/shared/ui/Skeleton';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

import { fieldErrorOf } from '../shared/fieldError';
import {
  AtivarVersao,
  HistoricoDoParametro,
  InserirTag,
  SemRotina,
  SituacaoDaModalidade,
  SituacaoDaVersao,
  VersoesDoParametro,
  Voltar,
} from './AnniversaryRenewalShared';
import {
  diasDoCampo,
  nomeDoIndice,
  taxasDoAniversario,
  useIndices,
} from './anniversaryRenewal';
import { tomDaParametrizacao } from './tonsDaParametrizacao';
import { Trilha } from './Trilha';

/**
 * Endosso de aniversario na parametrizacao (P-PAR-3): as telas
 * `/parametrizacao/endosso`, a configuracao do aniversario e as modalidades com o tipo
 * de indice, como no OnPoint. Tudo e parametro versionado; nenhum endosso de
 * aniversario e gerado e nenhum aviso e enviado ate a homologacao (END-007, PRC-009,
 * EMI-002).
 */

const PERM_VER = 'parametrizacao.aniversario.view';
const PERM_EDITAR = 'parametrizacao.aniversario.update';
const PERM_ATIVAR = 'parametrizacao.aniversario.approve';

const BASE = '/parametrizacao/endosso';
const CONFIGURACAO = `${BASE}/aniversario`;
const MODALIDADES = `${CONFIGURACAO}/modalidades`;

const trilhaDoEndosso = [
  { rotulo: 'Parametrizacao', para: '/parametrizacao' },
  { rotulo: 'Endosso', para: BASE },
];

const regrasPendentes = [
  'END-007',
  'END-008',
  'PRC-009',
  'EMI-002',
  'F-14',
  'PEN-R2-07',
];

function SemAcesso({ cabecalho }: { cabecalho: ReactNode }) {
  return (
    <div className="flex flex-col gap-4">
      {cabecalho}
      <Alert tone="info" title="Sem acesso">
        O seu acesso nao inclui os parametros do endosso de aniversario ({PERM_VER}).
      </Alert>
    </div>
  );
}

function situacaoDoParametro(dados: AnniversarySettings | undefined): string {
  if (!dados) return '';
  const partes: string[] = [];
  partes.push(
    dados.active_setting
      ? `Em vigor: versao ${dados.active_setting.version}`
      : 'Sem versao em vigor',
  );
  if (dados.draft_setting)
    partes.push(`rascunho ${dados.draft_setting.version} aberto`);
  return partes.join('; ');
}

// --- /parametrizacao/endosso ---------------------------------------------------------

/**
 * Tela "Endosso" da parametrizacao (PAR-149 a PAR-152): a secao "Cadastrados" com o
 * cartao "Endosso de Aniversario" e o "Historico de alteracoes".
 */
export function EndorsementParametersPage() {
  const { can } = useAuth();
  const podeLer = can(PERM_VER);
  const [vendoHistorico, setVendoHistorico] = useState(false);
  const consulta = useQuery({
    queryKey: ['anniversary-settings'],
    queryFn: ({ signal }) => anniversarySettings.get(signal),
    enabled: podeLer,
  });

  const cabecalho = (
    <>
      <Trilha
        itens={[{ rotulo: 'Parametrizacao', para: '/parametrizacao' }]}
        atual="Endosso"
      />
      <PageHeader
        title="Endosso"
        description="Parametros dos tipos de endosso. O endosso de aniversario e o parametrizado aqui."
        actions={
          podeLer ? (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setVendoHistorico(true)}
            >
              Historico de alteracoes
            </Button>
          ) : undefined
        }
      />
    </>
  );
  if (!podeLer) return <SemAcesso cabecalho={cabecalho} />;

  return (
    <div className="flex flex-col gap-5">
      {cabecalho}
      <SemRotina regras={regrasPendentes}>
        Os parametros ficam gravados com versao. O endosso de aniversario continua
        desligado no catalogo de endosso e nenhum aviso e enviado ate a homologacao.
      </SemRotina>
      {consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar o endosso de aniversario">
          {consulta.error.message}
        </Alert>
      ) : null}
      <section className="flex flex-col gap-3" aria-label="Cadastrados">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-500">
          Cadastrados
        </h3>
        <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <li className="surface-card flex h-full flex-col gap-3 p-5 transition-shadow duration-(--duration-base) hover:shadow-(--shadow-md)">
            {/* O chip tem a cor do assunto no menu da parametrizacao. */}
            <div className="flex items-center gap-3">
              <IconChip icon="file" tone={tomDaParametrizacao.endosso} />
              <Link
                to={CONFIGURACAO}
                className="text-sm font-semibold text-ink-900 hover:text-brand-700 hover:underline"
              >
                Endosso de Aniversario
              </Link>
            </div>
            <p className="text-xs text-ink-600">
              Dados do endosso, e-mail de aviso e objeto do endosso.
            </p>
            {consulta.isPending ? (
              <Skeleton className="h-4 w-40" />
            ) : (
              <Badge
                tone={consulta.data?.active_setting ? 'success' : 'neutral'}
                dot
                className="self-start"
              >
                {situacaoDoParametro(consulta.data)}
              </Badge>
            )}
            <Link
              to={MODALIDADES}
              className="text-xs font-medium text-brand-700 hover:underline"
            >
              Modalidades de aniversario
            </Link>
          </li>
        </ul>
      </section>
      {vendoHistorico ? (
        <HistoricoDoParametro
          chave={['anniversary-history', 'todos']}
          carregar={(signal) => anniversarySettings.history(undefined, signal)}
          onClose={() => setVendoHistorico(false)}
        />
      ) : null}
    </div>
  );
}

// --- /parametrizacao/endosso/aniversario --------------------------------------------

function textoDoNumero(valor: number | null | undefined): string {
  return valor === null || valor === undefined ? '' : String(valor);
}

function FormularioDoAniversario({ dados }: { dados: AnniversarySettings }) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const podeEditar = can(PERM_EDITAR);
  const podeAtivar = can(PERM_ATIVAR);
  const base = dados.draft_setting ?? dados.active_setting;
  const mensagemRef = useRef<RichTextEditorHandle>(null);
  const objetoRef = useRef<RichTextEditorHandle>(null);

  const [aviso, setAviso] = useState(textoDoNumero(base?.values.advance_notice_days));
  const [boleto, setBoleto] = useState(textoDoNumero(base?.values.boleto_due_days));
  const [taxa, setTaxa] = useState(base?.values.rate_source ?? '');
  const [titulo, setTitulo] = useState(base?.values.notice_email_subject ?? '');
  const [mensagem, setMensagem] = useState(base?.values.notice_email_message ?? '');
  const [objeto, setObjeto] = useState(base?.values.object_description ?? '');
  const [observacao, setObservacao] = useState(dados.draft_setting?.notes ?? '');
  const [ativando, setAtivando] = useState(false);

  function atualizar() {
    void queryClient.invalidateQueries({ queryKey: ['anniversary-settings'] });
    void queryClient.invalidateQueries({ queryKey: ['anniversary-versions'] });
    void queryClient.invalidateQueries({ queryKey: ['anniversary-history'] });
  }

  const salvar = useMutation({
    mutationFn: () =>
      anniversarySettings.saveDraft({
        advance_notice_days: diasDoCampo(aviso),
        boleto_due_days: diasDoCampo(boleto),
        rate_source: taxa === '' ? null : taxa,
        notice_email_subject: titulo,
        notice_email_message: mensagem,
        object_description: objeto,
        notes: observacao,
      }),
    onSuccess: (versao) => {
      toast.success(`Rascunho ${versao.version} salvo.`);
      atualizar();
    },
  });
  const apiError = salvar.error instanceof ApiError ? salvar.error : null;
  const erroDe = (campo: string) => fieldErrorOf(apiError, campo);
  const campos = [
    'advance_notice_days',
    'boleto_due_days',
    'rate_source',
    'notice_email_subject',
    'notice_email_message',
    'object_description',
  ];

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    salvar.mutate();
  }

  const desligado = !podeEditar;
  return (
    <div className="flex flex-col gap-5">
      <SituacaoDaVersao
        ativa={dados.active_setting}
        rascunho={dados.draft_setting}
        podeAtivar={podeAtivar}
        onAtivar={() => setAtivando(true)}
      />
      <form className="flex flex-col gap-5" onSubmit={enviar} noValidate>
        {salvar.error && !campos.some((campo) => erroDe(campo)) ? (
          <Alert tone="danger" title="Nao foi possivel salvar">
            {salvar.error.message}
          </Alert>
        ) : null}

        <Card>
          <CardHeader
            title="Dados do Endosso"
            description="Os campos do aniversario. Guardados sem efeito."
          />
          <CardBody className="grid gap-4 sm:grid-cols-3">
            <Field
              label="Notificacao previa do aniversario (dias)"
              type="number"
              inputMode="numeric"
              min={0}
              max={36500}
              value={aviso}
              disabled={desligado}
              error={erroDe('advance_notice_days')}
              onChange={(evento) => setAviso(evento.target.value)}
            />
            <Field
              label="Vencimento do boleto do endosso (dias)"
              type="number"
              inputMode="numeric"
              min={0}
              max={36500}
              value={boleto}
              disabled={desligado}
              error={erroDe('boleto_due_days')}
              onChange={(evento) => setBoleto(evento.target.value)}
            />
            <Select
              label="Taxa usada no endosso"
              placeholder="Nao informada"
              options={taxasDoAniversario}
              value={taxa}
              disabled={desligado}
              error={erroDe('rate_source')}
              onChange={(evento) => setTaxa(evento.target.value)}
            />
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Email de Aviso de Endosso"
            description="O envio fica desligado ate a homologacao (EMI-002). As TAGs sao as do e-mail."
          />
          <CardBody className="flex flex-col gap-4">
            <Field
              label="Titulo do email de aviso"
              required
              placeholder="Digite o titulo"
              maxLength={
                dados.active_setting?.values.notice_email_subject_max_characters ?? 200
              }
              value={titulo}
              disabled={desligado}
              error={erroDe('notice_email_subject')}
              onChange={(evento) => setTitulo(evento.target.value)}
            />
            <InserirTag
              contexto="email"
              rotulo="+ Inserir Tag no titulo"
              disabled={desligado}
              onIncluir={(marcacao) => setTitulo((atual) => `${atual}${marcacao}`)}
            />
            <InserirTag
              contexto="email"
              disabled={desligado}
              onIncluir={(marcacao) => mensagemRef.current?.inserirTexto(marcacao)}
            />
            <RichTextEditor
              label="Mensagem do email de aviso"
              required
              value={mensagem}
              onChange={setMensagem}
              maxLength={base?.values.notice_email_message_max_characters ?? 20000}
              disabled={desligado}
              error={erroDe('notice_email_message')}
              editorRef={mensagemRef}
              rows={8}
            />
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Objeto Endosso"
            description="Texto do objeto do endosso de aniversario, com as TAGs do objeto."
          />
          <CardBody className="flex flex-col gap-4">
            <InserirTag
              contexto="objeto"
              rotulo="+ Inserir Tag no objeto"
              disabled={desligado}
              onIncluir={(marcacao) => objetoRef.current?.inserirTexto(marcacao)}
            />
            <RichTextEditor
              label="Descricao do Objeto do Endosso"
              required
              value={objeto}
              onChange={setObjeto}
              maxLength={base?.values.object_description_max_characters ?? 50000}
              disabled={desligado}
              error={erroDe('object_description')}
              editorRef={objetoRef}
              rows={10}
            />
          </CardBody>
        </Card>

        {podeEditar ? (
          <Textarea
            label="Observacao da versao"
            maxLength={4000}
            value={observacao}
            onChange={(evento) => setObservacao(evento.target.value)}
          />
        ) : null}

        <div className="flex flex-wrap justify-between gap-2">
          <Voltar para={BASE} />
          {podeEditar ? (
            <Button type="submit" loading={salvar.isPending}>
              Salvar
            </Button>
          ) : null}
        </div>
      </form>

      {ativando && dados.draft_setting ? (
        <AtivarVersao
          versao={dados.draft_setting.version}
          onFechar={() => setAtivando(false)}
          ativar={(justificativa) => anniversarySettings.activate(justificativa)}
          onAtivado={() => {
            setAtivando(false);
            toast.success('Versao ativada.');
            atualizar();
          }}
        />
      ) : null}
    </div>
  );
}

function resumoDoAniversario(versao: AnniversaryVersion): string {
  const v = versao.values;
  return [
    v.advance_notice_days === null ? '' : `aviso ${v.advance_notice_days} d`,
    v.notice_email_subject ? `titulo "${v.notice_email_subject}"` : 'sem titulo',
    `objeto ${v.object_description_characters} caracteres`,
  ]
    .filter(Boolean)
    .join('; ');
}

/**
 * Configuracao do endosso de aniversario (`configurar-aniversario` do OnPoint, PAR-153 a
 * PAR-164): Dados do Endosso, Email de Aviso de Endosso com Titulo e Mensagem, Objeto
 * Endosso com a Descricao do Objeto, "+ Inserir Tag", Historico e Voltar.
 */
export function AnniversarySettingsPage() {
  const { can } = useAuth();
  const podeLer = can(PERM_VER);
  const [vendoHistorico, setVendoHistorico] = useState(false);
  const consulta = useQuery({
    queryKey: ['anniversary-settings'],
    queryFn: ({ signal }) => anniversarySettings.get(signal),
    enabled: podeLer,
  });
  const versoes = useQuery({
    queryKey: ['anniversary-versions'],
    queryFn: ({ signal }) => anniversarySettings.versions(signal),
    enabled: podeLer,
  });

  const cabecalho = (
    <>
      <Trilha itens={trilhaDoEndosso} atual="Endosso de Aniversario" />
      <PageHeader
        title="Endosso de Aniversario"
        description="Aviso por e-mail, objeto do endosso e os dados do endosso de aniversario."
        actions={
          podeLer ? (
            <>
              <Link
                to={MODALIDADES}
                className="text-xs font-medium text-brand-700 hover:underline"
              >
                Modalidades de aniversario
              </Link>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setVendoHistorico(true)}
              >
                Historico de alteracoes
              </Button>
            </>
          ) : undefined
        }
      />
    </>
  );
  if (!podeLer) return <SemAcesso cabecalho={cabecalho} />;

  return (
    <div className="flex flex-col gap-5">
      {cabecalho}
      <SemRotina regras={consulta.data?.pending_rules ?? regrasPendentes}>
        Ativar a versao grava o parametro e a trilha; nenhum endosso de aniversario e
        gerado e nenhum aviso e enviado.
      </SemRotina>
      {consulta.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar a configuracao">
          {consulta.error.message}
        </Alert>
      ) : (
        <FormularioDoAniversario
          key={`${consulta.data.draft_setting?.id ?? ''}-${consulta.data.draft_setting?.updated_at ?? ''}-${consulta.data.active_setting?.id ?? ''}`}
          dados={consulta.data}
        />
      )}
      <VersoesDoParametro
        versoes={versoes.data ?? []}
        carregando={versoes.isPending}
        erro={versoes.error}
        resumo={resumoDoAniversario}
      />
      {vendoHistorico ? (
        <HistoricoDoParametro
          chave={['anniversary-history', 'configuracao']}
          carregar={(signal) => anniversarySettings.history('configuracao', signal)}
          onClose={() => setVendoHistorico(false)}
        />
      ) : null}
    </div>
  );
}

// --- Modalidades de aniversario -----------------------------------------------------

/**
 * Lista das modalidades do endosso de aniversario (`modalidades-aniversario`, PAR-165 e
 * PAR-166): Modalidade, Tipo de Indice, Situacao, "Nova modalidade" e o historico.
 */
export function AnniversaryModalitiesPage() {
  const { can } = useAuth();
  const podeLer = can(PERM_VER);
  const podeEditar = can(PERM_EDITAR);
  const [vendoHistorico, setVendoHistorico] = useState(false);
  const indices = useIndices();
  const consulta = useQuery({
    queryKey: ['anniversary-modalities'],
    queryFn: ({ signal }) => anniversarySettings.listModalities(signal),
    enabled: podeLer,
  });

  const cabecalho = (
    <>
      <Trilha
        itens={[
          ...trilhaDoEndosso,
          { rotulo: 'Endosso de Aniversario', para: CONFIGURACAO },
        ]}
        atual="Modalidades de aniversario"
      />
      <PageHeader
        title="Modalidades de aniversario"
        description="As modalidades com endosso de aniversario e o indice de reajuste de cada uma."
        actions={
          podeLer ? (
            <>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setVendoHistorico(true)}
              >
                Historico de alteracoes
              </Button>
              {podeEditar ? (
                <Link
                  to={`${MODALIDADES}/nova`}
                  className="inline-flex h-8 items-center rounded-md bg-brand-600 px-3 text-xs font-semibold text-white hover:bg-brand-700"
                >
                  Nova modalidade
                </Link>
              ) : null}
            </>
          ) : undefined
        }
      />
    </>
  );
  if (!podeLer) return <SemAcesso cabecalho={cabecalho} />;

  return (
    <div className="flex flex-col gap-5">
      {cabecalho}
      {consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar as modalidades">
          {consulta.error.message}
        </Alert>
      ) : null}
      <Card>
        <DataTable
          columns={[
            {
              key: 'modalidade',
              header: 'Modalidade',
              render: (item: AnniversaryModality) => (
                <div className="min-w-0">
                  <p className="font-medium text-ink-900">
                    {item.modality.name || 'Modalidade sem cadastro'}
                  </p>
                  <p className="truncate text-2xs text-ink-500">
                    {item.modality.product_name}
                  </p>
                </div>
              ),
            },
            {
              key: 'indice',
              header: 'Tipo de Indice',
              render: (item: AnniversaryModality) =>
                nomeDoIndice(
                  indices.data,
                  (item.draft_setting ?? item.active_setting)?.values.economic_index ??
                    null,
                ),
            },
            {
              key: 'situacao',
              header: 'Situacao',
              render: (item: AnniversaryModality) => (
                <SituacaoDaModalidade
                  ativa={item.active_setting}
                  rascunho={item.draft_setting}
                />
              ),
            },
            {
              key: 'atualizado',
              header: 'Atualizado em',
              hideBelow: 'md',
              render: (item: AnniversaryModality) =>
                formatDateTime((item.draft_setting ?? item.active_setting)?.updated_at),
            },
            {
              key: 'acoes',
              header: 'Acoes',
              align: 'right',
              render: (item: AnniversaryModality) => (
                <LinkComIcone
                  acao="editar"
                  rotulo="Editar"
                  to={`${MODALIDADES}/${item.setting_id}`}
                />
              ),
            },
          ]}
          items={consulta.data ?? []}
          keyOf={(item) => item.modality.id}
          loading={consulta.isPending}
          empty={
            <EmptyState
              icon="layers"
              title="Nenhuma modalidade com endosso de aniversario"
              description="Use Nova modalidade para escolher a modalidade e o tipo de indice."
            />
          }
        />
      </Card>
      <div>
        <Voltar para={CONFIGURACAO} />
      </div>
      {vendoHistorico ? (
        <HistoricoDoParametro
          chave={['anniversary-history', 'modalidades']}
          carregar={(signal) => anniversarySettings.history('modalidades', signal)}
          onClose={() => setVendoHistorico(false)}
        />
      ) : null}
    </div>
  );
}

/**
 * Cadastro e edicao da modalidade de aniversario (`criar-modalidade-aniversario` e
 * `editar-modalidade-aniversario/:token`, PAR-167 a PAR-172): a secao "Dados da
 * Modalidade" com "Modalidade *" e "Tipo de Indice *".
 */
export function AnniversaryModalityFormPage() {
  const { settingId } = useParams();
  const novo = settingId === undefined;
  const { can } = useAuth();
  const podeLer = can(PERM_VER);
  const podeEditar = can(PERM_EDITAR);
  const podeAtivar = can(PERM_ATIVAR);
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const indices = useIndices();

  const detalhe = useQuery({
    queryKey: ['anniversary-modality', settingId],
    queryFn: ({ signal }) => anniversarySettings.getModality(settingId ?? '', signal),
    enabled: podeLer && !novo,
  });
  const cadastradas = useQuery({
    queryKey: ['anniversary-modalities'],
    queryFn: ({ signal }) => anniversarySettings.listModalities(signal),
    enabled: podeLer && novo,
  });
  const modalidades = useQuery({
    queryKey: ['modalities', 'ativas'],
    queryFn: ({ signal }) =>
      parametrization.listModalities({ status: 'ativo', limit: 200 }, signal),
    enabled: podeLer && novo && can('parametrizacao.modalidade.view'),
  });

  const cabecalho = (
    <>
      <Trilha
        itens={[
          ...trilhaDoEndosso,
          { rotulo: 'Endosso de Aniversario', para: CONFIGURACAO },
          { rotulo: 'Modalidades de aniversario', para: MODALIDADES },
        ]}
        atual={novo ? 'Nova modalidade' : 'Editar modalidade'}
      />
      <PageHeader
        title={novo ? 'Nova modalidade de aniversario' : 'Modalidade de aniversario'}
        description="Dados da Modalidade: a modalidade e o tipo de indice do reajuste."
      />
    </>
  );
  if (!podeLer) return <SemAcesso cabecalho={cabecalho} />;
  if (!novo && detalhe.isPending) {
    return (
      <div className="flex flex-col gap-4">
        {cabecalho}
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }
  if (!novo && detalhe.isError) {
    return (
      <div className="flex flex-col gap-4">
        {cabecalho}
        <Alert tone="danger" title="Nao foi possivel carregar a modalidade">
          {detalhe.error.message}
        </Alert>
      </div>
    );
  }

  const atual = detalhe.data?.draft_setting ?? detalhe.data?.active_setting ?? null;
  const jaCadastradas = new Set(
    (cadastradas.data ?? []).map((item) => item.modality.id),
  );
  const opcoesDeModalidade = (modalidades.data?.items ?? [])
    .filter((modalidade) => !jaCadastradas.has(modalidade.id))
    .map((modalidade) => ({ value: modalidade.id, label: modalidade.name }));

  return (
    <div className="flex flex-col gap-5">
      {cabecalho}
      <FormularioDaModalidade
        key={`${atual?.id ?? 'nova'}-${atual?.updated_at ?? ''}`}
        novo={novo}
        detalhe={detalhe.data}
        opcoesDeModalidade={opcoesDeModalidade}
        indices={(indices.data ?? [])
          .filter((indice) => indice.active)
          .map((indice) => ({ value: indice.code, label: indice.name }))}
        podeEditar={podeEditar}
        podeAtivar={podeAtivar}
        onSalvo={(id) => {
          void queryClient.invalidateQueries({ queryKey: ['anniversary-modalities'] });
          void queryClient.invalidateQueries({ queryKey: ['anniversary-modality'] });
          void queryClient.invalidateQueries({ queryKey: ['anniversary-history'] });
          toast.success('Rascunho salvo.');
          void navigate(`${MODALIDADES}/${id}`, { replace: !novo });
        }}
      />
    </div>
  );
}

function FormularioDaModalidade({
  novo,
  detalhe,
  opcoesDeModalidade,
  indices,
  podeEditar,
  podeAtivar,
  onSalvo,
}: {
  novo: boolean;
  detalhe: AnniversaryModality | undefined;
  opcoesDeModalidade: { value: string; label: string }[];
  indices: { value: string; label: string }[];
  podeEditar: boolean;
  podeAtivar: boolean;
  onSalvo: (settingId: string) => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const atual = detalhe?.draft_setting ?? detalhe?.active_setting ?? null;
  const [modalidade, setModalidade] = useState('');
  const [indice, setIndice] = useState(atual?.values.economic_index ?? '');
  const [observacao, setObservacao] = useState(detalhe?.draft_setting?.notes ?? '');
  const [ativando, setAtivando] = useState(false);

  const salvar = useMutation({
    mutationFn: () =>
      novo
        ? anniversarySettings.createModality({
            modality_id: modalidade,
            economic_index: indice === '' ? null : indice,
            notes: observacao,
          })
        : anniversarySettings.saveModality(detalhe?.setting_id ?? '', {
            economic_index: indice === '' ? null : indice,
            notes: observacao,
          }),
    onSuccess: (versao) => onSalvo(versao.id),
  });
  const apiError = salvar.error instanceof ApiError ? salvar.error : null;
  const desligado = !podeEditar;

  return (
    <div className="flex flex-col gap-5">
      {!novo && detalhe ? (
        <SituacaoDaVersao
          ativa={detalhe.active_setting}
          rascunho={detalhe.draft_setting}
          podeAtivar={podeAtivar}
          onAtivar={() => setAtivando(true)}
        />
      ) : null}
      <Card>
        <CardHeader title="Dados da Modalidade" />
        <CardBody>
          <form
            className="flex flex-col gap-4"
            noValidate
            onSubmit={(evento) => {
              evento.preventDefault();
              salvar.mutate();
            }}
          >
            {salvar.error &&
            !fieldErrorOf(apiError, 'modality_id') &&
            !fieldErrorOf(apiError, 'economic_index') ? (
              <Alert tone="danger" title="Nao foi possivel salvar">
                {salvar.error.message}
              </Alert>
            ) : null}
            <div className="grid gap-4 sm:grid-cols-2">
              {novo ? (
                <Select
                  label="Modalidade"
                  required
                  placeholder="Escolha a modalidade"
                  options={opcoesDeModalidade}
                  value={modalidade}
                  disabled={desligado}
                  error={fieldErrorOf(apiError, 'modality_id')}
                  onChange={(evento) => setModalidade(evento.target.value)}
                />
              ) : (
                <Field
                  label="Modalidade"
                  value={detalhe?.modality.name ?? ''}
                  disabled
                  readOnly
                />
              )}
              <Select
                label="Tipo de Indice"
                required
                placeholder="Escolha o tipo"
                options={indices}
                value={indice}
                disabled={desligado}
                error={fieldErrorOf(apiError, 'economic_index')}
                onChange={(evento) => setIndice(evento.target.value)}
              />
            </div>
            <p className="text-2xs text-ink-500">
              O indice fica guardado. Nenhum reajuste da importancia segurada e
              calculado ate a homologacao da formula (F-14).
            </p>
            {podeEditar ? (
              <Textarea
                label="Observacao da versao"
                maxLength={4000}
                value={observacao}
                onChange={(evento) => setObservacao(evento.target.value)}
              />
            ) : null}
            <div className="flex flex-wrap justify-between gap-2">
              <Voltar para={MODALIDADES} />
              {podeEditar ? (
                <Button
                  type="submit"
                  loading={salvar.isPending}
                  disabled={novo && (modalidade === '' || indice === '')}
                >
                  Salvar
                </Button>
              ) : null}
            </div>
          </form>
        </CardBody>
      </Card>
      {detalhe ? (
        <VersoesDoParametro
          versoes={detalhe.versions ?? []}
          carregando={false}
          erro={null}
          resumo={(versao) => `Indice: ${versao.values.economic_index ?? '-'}`}
        />
      ) : null}
      {ativando && detalhe?.draft_setting ? (
        <AtivarVersao
          versao={detalhe.draft_setting.version}
          onFechar={() => setAtivando(false)}
          ativar={(justificativa) =>
            anniversarySettings.activateModality(
              detalhe.draft_setting?.id ?? '',
              justificativa,
            )
          }
          onAtivado={() => {
            setAtivando(false);
            toast.success('Versao ativada.');
            void queryClient.invalidateQueries({ queryKey: ['anniversary-modality'] });
            void queryClient.invalidateQueries({
              queryKey: ['anniversary-modalities'],
            });
            void queryClient.invalidateQueries({ queryKey: ['anniversary-history'] });
          }}
        />
      ) : null}
    </div>
  );
}
